import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, PencilSimple, Trash, Wrench } from "@phosphor-icons/react";
import { Modal, Field } from "./Suppliers";
import { toast } from "sonner";

export default function Operations() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "operator";
  const canDelete = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => setItems((await api.get("/operations")).data);
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this operation? Existing schedules using it will still reference it.")) return;
    try { await api.delete(`/operations/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="operations-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-4xl uppercase tracking-tighter">Operations</h1>
          <p className="text-xs text-zinc-600 mt-1">Shop-floor operations that staff can be assigned to.</p>
        </div>
        {canEdit && (
          <button onClick={() => { setEditing(null); setOpen(true); }} className="brut-btn brut-btn-primary px-4 py-2 inline-flex items-center gap-2" data-testid="new-op-btn">
            <Plus size={14} weight="bold" /> Add Operation
          </button>
        )}
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="brut-table" data-testid="operations-table">
          <thead><tr><th>Code</th><th>Name</th><th>Produces Part</th><th>Description</th><th>Target/hr</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 text-zinc-500">No operations yet. Add your first operation (e.g. Bracket sub-assembly).</td></tr>}
            {items.map((op) => (
              <tr key={op.id} data-testid={`op-row-${op.code}`}>
                <td className="font-mono-ui font-bold inline-flex items-center gap-2"><Wrench size={14} weight="bold" /> {op.code}</td>
                <td>{op.name}</td>
                <td className="text-xs">
                  <div className="flex items-center gap-2">
                    <span className="w-4 h-4 border border-slate-400" style={{ background: op.color || "#94a3b8" }} title={op.color || "no color"}></span>
                    {op.produces_part_id ? <span className="brut-tag brut-tag-blue" data-testid={`op-linked-${op.code}`}>Linked</span> : <span className="text-zinc-400">—</span>}
                  </div>
                </td>
                <td className="text-xs text-zinc-600">{op.description || "—"}</td>
                <td className="font-mono-ui">{op.target_per_hour ? `${op.target_per_hour} pcs` : "—"}</td>
                <td>{op.active ? <span className="brut-tag brut-tag-green">Active</span> : <span className="brut-tag brut-tag-dark">Inactive</span>}</td>
                <td className="text-right whitespace-nowrap">
                  {canEdit && (
                    <button onClick={() => { setEditing(op); setOpen(true); }} className="brut-tag brut-tag-white mr-1" data-testid={`edit-op-${op.code}`}>
                      <PencilSimple size={10} weight="bold" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => remove(op.id)} className="brut-tag brut-tag-red" data-testid={`delete-op-${op.code}`}>
                      <Trash size={10} weight="bold" />
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {open && (
        <OpModal
          initial={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
          onSaved={() => { setOpen(false); setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function OpModal({ initial, onClose, onSaved }) {
  const [code, setCode] = useState(initial?.code || "");
  const [name, setName] = useState(initial?.name || "");
  const [description, setDescription] = useState(initial?.description || "");
  const [targetPerHour, setTargetPerHour] = useState(initial?.target_per_hour ?? "");
  const [producesPartId, setProducesPartId] = useState(initial?.produces_part_id || "");
  const [color, setColor] = useState(initial?.color || "#3b82f6");
  const [assembledParts, setAssembledParts] = useState([]);
  const [active, setActive] = useState(initial?.active ?? true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const r = await api.get("/parts");
      setAssembledParts(r.data.filter((p) => p.is_assembled));
    })();
  }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!code.trim() || !name.trim()) return toast.error("Code & Name are required");
    setSaving(true);
    try {
      const payload = {
        code: code.trim(), name: name.trim(), description,
        target_per_hour: targetPerHour === "" ? null : parseFloat(targetPerHour),
        produces_part_id: producesPartId || null,
        color,
        active,
      };
      if (initial?.id) await api.put(`/operations/${initial.id}`, payload);
      else await api.post("/operations", payload);
      toast.success(initial ? "Updated" : "Added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={initial ? "Edit Operation" : "Add Operation"} onClose={onClose}>
      <form onSubmit={save} className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Code *" required value={code} onChange={setCode} testid="op-code" placeholder="e.g. OP-BRK" />
          <Field label="Target/hr" value={targetPerHour} onChange={setTargetPerHour} testid="op-target" placeholder="pcs" />
        </div>
        <Field label="Name *" required value={name} onChange={setName} testid="op-name" placeholder="e.g. Bracket sub-assembly" />
        <Field label="Description" value={description} onChange={setDescription} textarea testid="op-desc" />
        <div>
          <label className="brut-label">Color <span className="text-[10px] text-zinc-500 font-normal">(shown on schedule & printouts)</span></label>
          <div className="flex flex-wrap gap-2 mt-1" data-testid="op-color-palette">
            {["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899", "#14b8a6", "#f97316", "#6366f1", "#0ea5e9"].map((c) => (
              <button type="button" key={c} onClick={() => setColor(c)} className={`w-8 h-8 border-2 ${color === c ? "border-slate-900 ring-2 ring-slate-900" : "border-slate-300"}`} style={{ background: c }} data-testid={`op-color-${c}`} aria-label={c} />
            ))}
            <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-8 h-8 border-2 border-slate-300 cursor-pointer" title="Custom color" />
          </div>
        </div>
        <div>
          <label className="brut-label">Produces Part <span className="text-[10px] text-zinc-500">(links output to Assembly Runs)</span></label>
          <select value={producesPartId} onChange={(e) => setProducesPartId(e.target.value)} className="brut-input mt-1" data-testid="op-produces-part">
            <option value="">— not linked —</option>
            {assembledParts.map((p) => <option key={p.id} value={p.id}>{p.part_number} — {p.name}</option>)}
          </select>
          <p className="text-[10px] text-zinc-500 mt-1">When linked, pieces produced by staff on this operation auto-attribute to any in-progress Assembly Run for the same part.</p>
        </div>
        <label className="inline-flex items-center gap-2 text-sm">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} data-testid="op-active" />
          Active (appears in schedule dropdown)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="brut-btn px-3 py-2">Cancel</button>
          <button type="submit" disabled={saving} className="brut-btn brut-btn-primary px-3 py-2" data-testid="save-op-btn">
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
